import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import axios from "axios";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../utils/constants";

export default function AdminOrderTime() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [time, setTime] = useState({ day: "1", time_from: "", time_to: "" });
  const [timeSlots, setTimeSlots] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addTimeFromRef = useRef(null);
  const addTimeToRef = useRef(null);
  const editTimeFromRef = useRef(null);
  const editTimeToRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = Cookies.get("user")
          ? JSON.parse(Cookies.get("user"))
          : null;
        if (!user?.id) return navigate("/admin");

        const {
          data: { permissions },
        } = await axios.get(`${CONSTANTS.baseRoute}/permissions`, {
          params: { user_id: user.id },
        });
        setUserPermissions(permissions);

        if (!permissions.includes(27)) {
          setMessage({ type: "danger", text: t("orderTime.noPermissionView") });
          setTimeout(() => navigate("/admin/home"), 2500);
          return;
        }

        await fetchTimeSlots();
      } catch (error) {
        console.error("Error fetching initial data:", error);
        setMessage({ type: "danger", text: t("orderTime.fetchError") });
        if ([401, 403].includes(error.response?.status)) navigate("/admin");
      }
    };

    fetchData();
  }, [navigate, t]);

  // Flatpickr for Add form
  useEffect(() => {
    let fpAddFrom, fpAddTo;
    if (
      userPermissions.includes(28) &&
      addTimeFromRef.current &&
      addTimeToRef.current
    ) {
      fpAddFrom = flatpickr(addTimeFromRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        onChange: (_, dateStr) =>
          setTime((prev) => ({ ...prev, time_from: dateStr })),
      });

      fpAddTo = flatpickr(addTimeToRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        onChange: (_, dateStr) =>
          setTime((prev) => ({ ...prev, time_to: dateStr })),
      });
    }

    return () => {
      fpAddFrom?.destroy();
      fpAddTo?.destroy();
    };
  }, [userPermissions]);

  // Flatpickr for Edit modal
  useEffect(() => {
    let fpEditFrom, fpEditTo;
    if (
      showEditModal &&
      editingSlot &&
      editTimeFromRef.current &&
      editTimeToRef.current
    ) {
      fpEditFrom = flatpickr(editTimeFromRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        defaultDate: editingSlot.time_from,
        onChange: (_, dateStr) =>
          setEditingSlot((prev) => ({ ...prev, time_from: dateStr })),
      });

      fpEditTo = flatpickr(editTimeToRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        defaultDate: editingSlot.time_to,
        onChange: (_, dateStr) =>
          setEditingSlot((prev) => ({ ...prev, time_to: dateStr })),
      });
    }

    return () => {
      fpEditFrom?.destroy();
      fpEditTo?.destroy();
    };
  }, [showEditModal, editingSlot]);

  const fetchTimeSlots = async () => {
    try {
      const { data } = await axios.get(`${CONSTANTS.baseRoute}/order/times`);
      setTimeSlots(data);
    } catch (error) {
      console.error(error);
      setMessage({ type: "danger", text: t("orderTime.timesFetchError") });
    }
  };

  const hasPermission = (id) => userPermissions.includes(id);

  const dayLabel = (day) =>
    day === "tomorrow" ? t("orderTime.tomorrow") : t("orderTime.today");

  const showNotification = (type, text, duration = 3500) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), duration);
  };

  const handleAddTime = async () => {
    if (!hasPermission(28))
      return showNotification("danger", t("orderTime.noPermissionAdd"));

    if (!time.time_from || !time.time_to) {
      return showNotification("danger", t("orderTime.allFieldsRequired"));
    }

    setIsSubmitting(true);
    try {
      const payload = {
        day: time.day === "1" ? "today" : "tomorrow",
        time_from: time.time_from,
        time_to: time.time_to,
      };

      const { data } = await axios.post(
        `${CONSTANTS.baseRoute}/order/times`,
        payload
      );
      setTimeSlots((prev) => [...prev, data]);
      setTime({ day: "1", time_from: "", time_to: "" });
      if (addTimeFromRef.current) addTimeFromRef.current.value = "";
      if (addTimeToRef.current) addTimeToRef.current.value = "";
      showNotification("success", t("orderTime.timeAddedSuccess"));
    } catch (error) {
      showNotification("danger", t("orderTime.timeAddError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (slot) => {
    if (!hasPermission(29))
      return showNotification("danger", t("orderTime.noPermissionEdit"));

    setEditingSlot({
      ...slot,
      day: slot.day === "tomorrow" ? "2" : "1",
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!hasPermission(29))
      return showNotification("danger", t("orderTime.noPermissionEdit"));
    if (!editingSlot?.time_from || !editingSlot?.time_to) {
      return showNotification("danger", t("orderTime.allFieldsRequired"));
    }

    setIsSubmitting(true);
    try {
      const payload = {
        day: editingSlot.day === "2" ? "tomorrow" : "today",
        time_from: editingSlot.time_from,
        time_to: editingSlot.time_to,
        is_active: editingSlot.is_active,
      };

      const { data } = await axios.put(
        `${CONSTANTS.baseRoute}/order/times/${editingSlot.id}`,
        payload
      );

      setTimeSlots((prev) =>
        prev.map((s) => (s.id === editingSlot.id ? data : s))
      );
      setShowEditModal(false);
      setEditingSlot(null);
      showNotification("success", t("orderTime.timeUpdatedSuccess"));
    } catch (error) {
      showNotification("danger", t("orderTime.timeUpdateError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!hasPermission(30))
      return showNotification("danger", t("orderTime.noPermissionDelete"));
    if (!window.confirm(t("orderTime.confirmDelete"))) return;

    try {
      await axios.delete(`${CONSTANTS.baseRoute}/order/times/${id}`);
      setTimeSlots((prev) => prev.filter((s) => s.id !== id));
      setShowEditModal(false);
      setEditingSlot(null);
      showNotification("success", t("orderTime.timeDeletedSuccess"));
    } catch (error) {
      showNotification("danger", t("orderTime.timeDeleteError"));
    }
  };

  const handleToggleActive = async (slot) => {
    if (!hasPermission(29))
      return showNotification("danger", t("orderTime.noPermissionEdit"));

    try {
      const { data } = await axios.put(
        `${CONSTANTS.baseRoute}/order/times/${slot.id}/toggle-active`,
        { is_active: !slot.is_active }
      );

      setTimeSlots((prev) => prev.map((s) => (s.id === slot.id ? data : s)));
      showNotification(
        "success",
        t("orderTime.toggleSuccess", {
          status: !slot.is_active
            ? t("common.activated")
            : t("common.deactivated"),
        })
      );
    } catch (error) {
      showNotification("danger", t("orderTime.toggleError"));
    }
  };

  return (
    <div className="admin-main ps-2 pe-3 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn admin-icon-btn"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <button
            className="btn admin-icon-btn"
            onClick={() => navigate(+1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
          <h4 className="admin-page-title">{t("orderTime.title")}</h4>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div
          className={`alert alert-${message.type}  mb-4 alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
          ></button>
        </div>
      )}

      <div className="row g-4">
        {/* Time Slots List */}
        <div className={hasPermission(28) ? "col-lg-7" : "col-12"}>
          <div className="admin-card overflow-hidden">
            <div
              className="admin-card-header d-flex justify-content-between align-items-center"
            >
              <span>
                <i className="bi bi-clock-history me-2"></i>
                {t("orderTime.existingTimeSlots")}
              </span>
              <span className="badge bg-light text-dark fs-6">
                {timeSlots.length}
              </span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 text-center align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>{t("orderTime.day")}</th>
                      <th>{t("orderTime.startTime")}</th>
                      <th>{t("orderTime.endTime")}</th>
                      <th>{t("orderTime.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.length > 0 ? (
                      timeSlots.map((slot) => (
                        <tr
                          key={slot.id}
                          style={{
                            cursor: hasPermission(29) ? "pointer" : "default",
                          }}
                          onClick={() =>
                            hasPermission(29) && handleEditClick(slot)
                          }
                        >
                          <td>
                            <span className="status-pill status-pending">
                              {dayLabel(slot.day)}
                            </span>
                          </td>
                          <td className="fw-medium">{slot.time_from}</td>
                          <td className="fw-medium">{slot.time_to}</td>
                          <td>
                            <button
                              className="btn btn-link p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleActive(slot);
                              }}
                              disabled={!hasPermission(29)}
                            >
                              <i
                                className={`bi bi-${
                                  slot.is_active
                                    ? "check-circle-fill text-success"
                                    : "x-circle-fill text-danger"
                                }`}
                                style={{ fontSize: "1.4rem" }}
                              ></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="py-5 text-muted">
                          <i className="bi bi-clock display-4 mb-3 d-block text-muted"></i>
                          {t("orderTime.noTimeSlotsFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Add New Time Slot Form */}
        {hasPermission(28) && (
          <div className="col-lg-5">
            <div className="admin-card">
              <div
                className="admin-card-header text-center"
              >
                <i className="bi bi-plus-circle me-2"></i>
                {t("orderTime.addNewTimeSlot")}
              </div>
              <div className="card-body p-4">
                <form>
                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      {t("orderTime.day")}
                    </label>
                    <select
                      className="form-select"
                      value={time.day}
                      onChange={(e) =>
                        setTime((prev) => ({ ...prev, day: e.target.value }))
                      }
                    >
                      <option value="1">{t("orderTime.today")}</option>
                      <option value="2">{t("orderTime.tomorrow")}</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      {t("orderTime.startTime")}
                    </label>
                    <input
                      ref={addTimeFromRef}
                      className="form-control "
                      placeholder="HH:MM"
                      value={time.time_from}
                      onChange={(e) =>
                        setTime((prev) => ({
                          ...prev,
                          time_from: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">
                      {t("orderTime.endTime")}
                    </label>
                    <input
                      ref={addTimeToRef}
                      className="form-control "
                      placeholder="HH:MM"
                      value={time.time_to}
                      onChange={(e) =>
                        setTime((prev) => ({
                          ...prev,
                          time_to: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <button
                    type="button"
                    className="btn btn-success w-100 py-2 fw-bold "
                    onClick={handleAddTime}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.saving")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plus-circle me-2"></i>
                        {t("common.add")}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingSlot && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          onClick={(e) =>
            e.target.classList.contains("modal") && setShowEditModal(false)
          }
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content rounded-4 shadow-xl border-0">
              <div
                className="admin-card-header d-flex justify-content-between"
              >
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-pencil me-2"></i>
                  {t("orderTime.editTimeSlot")}
                </h5>
                <div className="">
                  {hasPermission(30) && (
                    <button
                      className="btn btn-sm btn-danger me-2"
                      onClick={() => handleDelete(editingSlot.id)}
                    >
                      <i className="bi bi-trash me-1"></i>
                      {t("common.delete")}
                    </button>
                  )}
                  
                </div>
              </div>

              <div className="modal-body p-4">
                <div className="mb-4">
                  <label className="form-label fw-bold">
                    {t("orderTime.day")}
                  </label>
                  <select
                    className="form-select"
                    value={editingSlot.day}
                    onChange={(e) =>
                      setEditingSlot((prev) => ({
                        ...prev,
                        day: e.target.value,
                      }))
                    }
                  >
                    <option value="1">{t("orderTime.today")}</option>
                    <option value="2">{t("orderTime.tomorrow")}</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    {t("orderTime.startTime")}
                  </label>
                  <input
                    ref={editTimeFromRef}
                    className="form-control "
                    value={editingSlot.time_from}
                    onChange={(e) =>
                      setEditingSlot((prev) => ({
                        ...prev,
                        time_from: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    {t("orderTime.endTime")}
                  </label>
                  <input
                    ref={editTimeToRef}
                    className="form-control "
                    value={editingSlot.time_to}
                    onChange={(e) =>
                      setEditingSlot((prev) => ({
                        ...prev,
                        time_to: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="edit_is_active"
                    checked={editingSlot.is_active}
                    onChange={(e) =>
                      setEditingSlot((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                  <label
                    className="form-check-label fw-bold"
                    htmlFor="edit_is_active"
                  >
                    {t("orderTime.active")}
                  </label>
                </div>
              </div>

              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={() => setShowEditModal(false)}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-success px-4 fw-bold"
                  onClick={handleSaveEdit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t("common.saving")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      {t("common.save")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
